import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { ProjectSettlementsCommentsService } from "@/services/projectSettlementsComments";
import { SuccessFetchListResponse } from "@/services/api";
import { useSettlementId } from "@/modules/projects/hooks/useSettlementId";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import type { ReadProjectSettlementsComments } from "@/schemas/projectSettlementsComments";
import { mapProjectSettlementCommentsToCommentRead } from "../../../utils/mapProjectSettlementCommentsToCommentRead";
import type { CommentRead } from "@/schemas/comments";
import { CommentsList } from "@/modules/comments";
import { AddCommentCreator } from "@/modules/projects/components/settlementsDetails/AddCommentCreator";
import { toast } from "@/components/ui/use-toast";

const CommentsSectionComponent = () => {
  const { t } = useTranslation();
  const settlementId = useSettlementId();

  const name = "projectSettlementsComments";
  const options = useCreateDataTableOptions<CommentRead>(name);

  const queryKey = useDynamicQueryParams([name, settlementId], options);
  const queryFn = useCallback(
    ({ pageParam }: { pageParam: string | undefined }) =>
      ProjectSettlementsCommentsService.getList(settlementId, {
        lastItem: pageParam,
        filters: options.filters,
        sorting: options.sorting[0],
      }),
    [settlementId, options.filters, options.sorting],
  );

  const getNextPageParam = useCallback(
    (lastPage: SuccessFetchListResponse<ReadProjectSettlementsComments>) =>
      lastPage.last_item,
    [],
  );

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    initialPageParam: undefined,
    getNextPageParam,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const onSuccess = useCallback(() => {
    toast({
      title: t("projectSettlementsComments.success"),
    });
    return query.refetch();
  }, [query, t]);

  return (
    <CommentsList
      name="project-settlements-comments"
      query={query}
      options={options}
      title={t("projectSettlementsComments.header")}
      initialColumnVisibility={{ author_role_name: false }}
      dataMapper={mapProjectSettlementCommentsToCommentRead}
      footer={
        <AddCommentCreator
          settlementId={settlementId}
          isPending={query.isLoading}
          onSuccess={onSuccess}
          errorTitle={t("projectSettlementsComments.error")}
        />
      }
    />
  );
};

export const CommentsSection = memo(CommentsSectionComponent);
